import React, { useState, useEffect } from "react";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api.ts";
import { formatPrice } from "../../utils/functions.js";
import {
  unsolvedvehicales,
  derhumSign,
  soldvehical,
  totavehical,
  totalearning,
} from "../../utils/data";
import placeholder from "../../assets/Car place holder PNG.png";

const StatCard = ({
  title,
  value,
  percentage,
  icon,
  bgStyle,
  svgPath,
  svgPosition,
  aedicon,
}) => (
  <div
    className='relative rounded-lg sm:rounded-xl md:rounded-2xl p-3 sm:p-4 text-white overflow-hidden w-full'
    style={{ background: bgStyle, minHeight: "120px", height: "auto" }}
  >
    {/* Background SVG with custom position */}
    <div
      className={`${svgPosition} opacity-20 sm:opacity-30 md:opacity-40 md:h-[200px]`}
    >
      <svg
        className='w-[4rem] h-[4rem] sm:w-[6rem] sm:h-[6rem] md:w-[10rem] md:h-[10rem] lg:w-[12rem] lg:h-[12rem]'
        viewBox='0 0 243 154'
        fill='none'
      >
        <path
          d={svgPath}
          stroke='white'
          strokeOpacity='0.5'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeDasharray='8 4'
        />
      </svg>
    </div>

    {/* Content */}
    <div className='relative z-10'>
      <div className='flex items-start justify-between mb-1.5 sm:mb-2'>
        <div className='bg-white h-[36px] w-[36px] sm:h-[40px] sm:w-[40px] md:h-[48px] md:w-[48px] flex justify-center items-center rounded-xl sm:rounded-2xl flex-shrink-0 overflow-hidden'>
          <div className='w-10 h-10 sm:w-10 sm:h-10 md:w-10 md:h-10 flex items-center justify-center'>
            {icon}
          </div>
        </div>
        {percentage && (
          <div className='bg-transparent border border-white px-2 py-1 sm:p-2 md:p-3 rounded-full text-xs sm:text-sm md:text-[16px] font-semibold flex-shrink-0'>
            {percentage}
          </div>
        )}
      </div>
      <h3 className='text-xs sm:text-sm md:text-base font-medium mb-0.5 sm:mb-1'>
        {title}
      </h3>
      <p className='text-base sm:text-lg md:text-xl flex items-center gap-1 font-bold break-words'>
        {aedicon}
        <span className='truncate'>{value}</span>
      </p>
    </div>
  </div>
);

const BuyerDashboard = () => {
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wonVehicles, setWonVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    invoiceTotalAmount: 0,
    paidInvoices: 0,
    partialPaidInvoices: 0,
    pendingInvoices: 0,
    pendingPaymentRequests: 0,
    pendingPaymentRequestsTotal: 0,
    remainingCarLimit: 0,
    remainingCreditLimit: 0,
    remainingDeposit: 0,
    totalPaidAmount: 0,
    totalRemainingAmount: 0,
    totalVATPaid: 0,
    underApprovalVehiclesCount: 0,
    wonVehiclesCount: 0,
  });

  // Fetch dashboard statistics
  const [previousWeekStats, setPreviousWeekStats] = useState({
    invoiceTotalAmount: 0,
    paidInvoices: 0,
    partialPaidInvoices: 0,
    pendingInvoices: 0,
    pendingPaymentRequests: 0,
    pendingPaymentRequestsTotal: 0,
    remainingCarLimit: 0,
    remainingCreditLimit: 0,
    remainingDeposit: 0,
    totalPaidAmount: 0,
    totalRemainingAmount: 0,
    totalVATPaid: 0,
    underApprovalVehiclesCount: 0,
    wonVehiclesCount: 0,
  });

  const [percentages, setPercentages] = useState({
    wonVehicles: 0,
    underApproval: 0,
    pendingInvoices: 0,
    remainingCarLimit: 0,
    totalPayment: 0,
    paidInvoices: 0,
    invoiceTotal: 0,
    remainingCredit: 0,
  });

  // Calculate percentage change
  const calculatePercentage = (current, previous) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  // Fetch won vehicles and statistics
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch current stats
        const currentStatsResponse = await api.get(
          `/stats/buyer/${user.buyer.id}/totalcounts`
        );

        // Calculate date ranges for comparison
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days ago
        const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); // 30 days ago

        // Format dates as y-m-d
        const formatDate = (date) => {
          return date.toISOString().split("T")[0];
        };

        // Fetch previous week stats using date range
        const previousWeekResponse = await api.get(
          `/stats/buyer/${user.buyer.id}/totalcounts`,
          {
            params: {
              startDate: formatDate(oneWeekAgo),
              endDate: formatDate(now),
            },
          }
        );

        // Fetch previous month stats using date range
        const previousMonthResponse = await api.get(
          `/stats/buyer/${user.buyer.id}/totalcounts`,
          {
            params: {
              startDate: formatDate(oneMonthAgo),
              endDate: formatDate(oneWeekAgo),
            },
          }
        );

        // Fetch paid won vehicles for the dashboard list
        const paidWonVehiclesResponse = await api.get(`/users/mywonvehicles`, {
          params: {
            page: 1,
            limit: 10,
            orderBy: "id",
            order: "desc",
            "filters[isSold]": 1,
          },
        });

        const rawWonVehicles = paidWonVehiclesResponse?.data?.data || [];

        // Filter only Paid invoices and normalize shape for the table
        const paidWonVehicles = rawWonVehicles
          .filter((row) => {
            const statusName =
              row?.buyerInvoiceDetails?.[0]?.buyerInvoice?.invoicePaymentStatus
                ?.name;
            return statusName === "Paid";
          })
          .map((row) => {
            const v = row?.vehicle || row; // some APIs embed vehicle under row.vehicle
            return {
              id: v?.id ?? row?.id,
              name: v?.name ?? "-",
              year: v?.year,
              lotNumber: v?.lotNumber,
              vinNumber: v?.vinNumber,
              images: Array.isArray(v?.images) ? v.images : [],
              soldPrice: row?.soldPrice ?? v?.soldPrice ?? 0,
            };
          });

        setWonVehicles(paidWonVehicles);

        // Set current API response data to stats
        const currentApiData = currentStatsResponse.data;
        const currentStats = {
          invoiceTotalAmount: currentApiData.invoiceTotalAmount || 0,
          paidInvoices: currentApiData.paidInvoices || 0,
          partialPaidInvoices: currentApiData.partialPaidInvoices || 0,
          pendingInvoices: currentApiData.pendingInvoices || 0,
          pendingPaymentRequests: currentApiData.pendingPaymentRequests || 0,
          pendingPaymentRequestsTotal:
            currentApiData.pendingPaymentRequestsTotal || 0,
          remainingCarLimit: currentApiData.remainingCarLimit || 0,
          remainingCreditLimit: currentApiData.remainingCreditLimit || 0,
          remainingDeposit: currentApiData.remainingDeposit || 0,
          totalPaidAmount: currentApiData.totalPaidAmount || 0,
          totalRemainingAmount: currentApiData.totalRemainingAmount || 0,
          totalVATPaid: currentApiData.totalVATPaid || 0,
          underApprovalVehiclesCount:
            currentApiData.underApprovalVehiclesCount || 0,
          wonVehiclesCount: currentApiData.wonVehiclesCount || 0,
        };
        setStats(currentStats);

        // Set previous month API response data (using month data for better comparison)
        const previousApiData = previousMonthResponse.data;
        const previousStats = {
          invoiceTotalAmount: previousApiData.invoiceTotalAmount || 0,
          paidInvoices: previousApiData.paidInvoices || 0,
          partialPaidInvoices: previousApiData.partialPaidInvoices || 0,
          pendingInvoices: previousApiData.pendingInvoices || 0,
          pendingPaymentRequests: previousApiData.pendingPaymentRequests || 0,
          pendingPaymentRequestsTotal:
            previousApiData.pendingPaymentRequestsTotal || 0,
          remainingCarLimit: previousApiData.remainingCarLimit || 0,
          remainingCreditLimit: previousApiData.remainingCreditLimit || 0,
          remainingDeposit: previousApiData.remainingDeposit || 0,
          totalPaidAmount: previousApiData.totalPaidAmount || 0,
          totalRemainingAmount: previousApiData.totalRemainingAmount || 0,
          totalVATPaid: previousApiData.totalVATPaid || 0,
          underApprovalVehiclesCount:
            previousApiData.underApprovalVehiclesCount || 0,
          wonVehiclesCount: previousApiData.wonVehiclesCount || 0,
        };
        setPreviousWeekStats(previousStats);

        // Calculate percentages
        const calculatedPercentages = {
          wonVehicles: calculatePercentage(
            currentStats.wonVehiclesCount,
            previousStats.wonVehiclesCount
          ),
          underApproval: calculatePercentage(
            currentStats.underApprovalVehiclesCount,
            previousStats.underApprovalVehiclesCount
          ),
          pendingInvoices: calculatePercentage(
            currentStats.pendingInvoices,
            previousStats.pendingInvoices
          ),
          remainingCarLimit: calculatePercentage(
            currentStats.remainingCarLimit,
            previousStats.remainingCarLimit
          ),
          totalPayment: calculatePercentage(
            currentStats.totalPaidAmount,
            previousStats.totalPaidAmount
          ),
          paidInvoices: calculatePercentage(
            currentStats.paidInvoices,
            previousStats.paidInvoices
          ),
          invoiceTotal: calculatePercentage(
            currentStats.invoiceTotalAmount,
            previousStats.invoiceTotalAmount
          ),
          remainingCredit: calculatePercentage(
            currentStats.remainingCreditLimit,
            previousStats.remainingCreditLimit
          ),
        };
        setPercentages(calculatedPercentages);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchDashboardData();
    }
  }, [user?.id]);

  // get last 3 won vehicles
  useEffect(() => {
    const fetchWonVehicles = async () => {
      try {
        setLoading(true);

        // Fetch won vehicles
        const response = await api.get(`/users/mywonvehicles`, {
          params: { limit: 3 },
        });

        // Set API response data to stats
        const apiData = response.data.data || response.data;
        setWonVehicles(Array.isArray(apiData) ? apiData : []);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching won vehicles:", error);
        setLoading(false);
      }
    };

    if (user?.id) {
      fetchWonVehicles();
    }
  }, [user?.id]);

  return (
    <div dir={isRTL ? "rtl" : "ltr"} lang={language}>
      {/* Vehicle-related Cards */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8 mb-4 sm:mb-5 md:mb-6'>
        <StatCard
          title={translate("dashboard.stats.wonVehicles")}
          value={loading ? "..." : stats.wonVehiclesCount.toString()}
          percentage={
            loading
              ? "..."
              : `${percentages.wonVehicles >= 0 ? "+" : ""}${
                  percentages.wonVehicles
                }%`
          }
          icon={soldvehical}
          bgStyle='linear-gradient(280.11deg,rgb(137, 116, 80) -12.68%,rgb(62, 35, 4) 100.28%)'
          svgPath='M120.484 118.414C110.796 126.656 88.9216 139.344 78.9244 124.162C68.9272 108.98 79.3952 85.8114 85.8787 76.1247C91.9892 67.2996 95.193 50.4551 59.1227 53.6804C14.0348 57.712 11.039 34.2721 8.43518 5.15247M18.4909 11.9301L8.11472 1.56852L1.52713 13.447'
          svgPosition='absolute right-[-8px] sm:right-[-15px] md:right-[-20px] bottom-[-25px] sm:bottom-[-40px] md:bottom-[-55px]'
        />
        <StatCard
          title={translate("dashboard.stats.underApproval")}
          value={loading ? "..." : stats.underApprovalVehiclesCount.toString()}
          percentage={
            loading
              ? "..."
              : `${percentages.underApproval >= 0 ? "+" : ""}${
                  percentages.underApproval
                }%`
          }
          icon={unsolvedvehicales}
          bgStyle='linear-gradient(280.11deg,rgb(107, 114, 128) -12.68%,rgb(75, 85, 99) 100.28%)'
          svgPath='M1.97656 1.09766C0.373569 17.8937 9.66506 52.2038 59.6554 55.0739C109.646 57.944 119.776 100.819 118.59 121.897M118.59 121.897L109.537 110.976M118.59 121.897L125.788 110.722'
          svgPosition='absolute left-[50px] sm:left-[80px] md:left-[110px] top-[-10px] sm:top-[-15px] md:top-[-20px]'
        />
        <StatCard
          title={translate("dashboard.stats.pendingInvoices")}
          value={loading ? "..." : stats.pendingInvoices.toString()}
          percentage={
            loading
              ? "..."
              : `${percentages.pendingInvoices >= 0 ? "+" : ""}${
                  percentages.pendingInvoices
                }%`
          }
          icon={unsolvedvehicales}
          bgStyle='linear-gradient(280.11deg, #EF4444 -12.68%,rgb(94, 16, 16) 100.28%)'
          svgPath='M1.97656 1.09766C0.373569 17.8937 9.66506 52.2038 59.6554 55.0739C109.646 57.944 119.776 100.819 118.59 121.897M118.59 121.897L109.537 110.976M118.59 121.897L125.788 110.722'
          svgPosition='absolute left-[50px] sm:left-[80px] md:left-[110px] top-[-10px] sm:top-[-15px] md:top-[-20px]'
        />
        <StatCard
          title={translate("dashboard.stats.remainingCarLimit")}
          value={loading ? "..." : stats.remainingCarLimit.toString()}
          percentage={
            loading
              ? "..."
              : `${percentages.remainingCarLimit >= 0 ? "+" : ""}${
                  percentages.remainingCarLimit
                }%`
          }
          icon={totavehical}
          bgStyle='linear-gradient(280.11deg,rgb(54, 254, 187) -12.68%, #059669 100.28%)'
          svgPath='M94 145C95.8713 130.63 110.343 103.664 153.257 110.76C196.172 117.855 213.001 83.8763 216.05 66M216.05 66L206.277 73.4256M216.05 66L220 76.7259'
          svgPosition='absolute left-[25px] sm:left-[35px] md:left-[50px] top-[-15px] sm:top-[-22px] md:top-[-29px]'
        />
      </div>

      {/* Payment-related Cards */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8 mb-4 sm:mb-6 md:mb-8'>
        <StatCard
          title={translate("dashboard.stats.totalPayment")}
          value={
            loading
              ? "..."
              : `${Number(stats.totalPaidAmount).toLocaleString("en-AE", {
                  maximumFractionDigits: 0,
                })}`
          }
          percentage={
            loading
              ? "..."
              : `${percentages.totalPayment >= 0 ? "+" : ""}${
                  percentages.totalPayment
                }%`
          }
          aedicon={<>{derhumSign}</>}
          icon={totalearning}
          bgStyle='linear-gradient(280.11deg,rgb(139, 151, 171) -12.68%,rgb(16, 20, 32) 100.28%)'
          svgPath='M0.999699 1.00016C26.9471 6.49534 97.1448 21.4249 87.4724 53.7082C80.8686 75.7493 56.5904 63.6007 64.7409 39.7722C72.8914 15.9437 115.65 15.7066 119.4 64.9586M119.4 64.9586L111.04 59.6056M119.4 64.9586L126.125 53.5401'
          svgPosition='absolute left-[2.5rem] sm:left-[4rem] md:left-[6rem] top-0'
        />
        <StatCard
          title={translate("dashboard.stats.paidInvoices")}
          value={loading ? "..." : stats.paidInvoices.toString()}
          percentage={
            loading
              ? "..."
              : `${percentages.paidInvoices >= 0 ? "+" : ""}${
                  percentages.paidInvoices
                }%`
          }
          icon={totalearning}
          bgStyle='linear-gradient(280.11deg,rgb(34, 197, 94) -12.68%,rgb(21, 128, 61) 100.28%)'
          svgPath='M0.999699 1.00016C26.9471 6.49534 97.1448 21.4249 87.4724 53.7082C80.8686 75.7493 56.5904 63.6007 64.7409 39.7722C72.8914 15.9437 115.65 15.7066 119.4 64.9586M119.4 64.9586L111.04 59.6056M119.4 64.9586L126.125 53.5401'
          svgPosition='absolute left-[2.5rem] sm:left-[4rem] md:left-[6rem] top-0'
        />
        <StatCard
          title={translate("dashboard.stats.invoiceTotal")}
          value={
            loading
              ? "..."
              : `${Number(stats.invoiceTotalAmount).toLocaleString("en-AE", {
                  maximumFractionDigits: 0,
                })}`
          }
          percentage={
            loading
              ? "..."
              : `${percentages.invoiceTotal >= 0 ? "+" : ""}${
                  percentages.invoiceTotal
                }%`
          }
          aedicon={<>{derhumSign}</>}
          icon={totalearning}
          bgStyle='linear-gradient(280.11deg,rgb(99, 102, 241) -12.68%,rgb(67, 56, 202) 100.28%)'
          svgPath='M0.999699 1.00016C26.9471 6.49534 97.1448 21.4249 87.4724 53.7082C80.8686 75.7493 56.5904 63.6007 64.7409 39.7722C72.8914 15.9437 115.65 15.7066 119.4 64.9586M119.4 64.9586L111.04 59.6056M119.4 64.9586L126.125 53.5401'
          svgPosition='absolute left-[2.5rem] sm:left-[4rem] md:left-[6rem] top-0'
        />
        <StatCard
          title={translate("dashboard.stats.remainingCredit")}
          value={
            loading
              ? "..."
              : `${Number(stats.remainingCreditLimit).toLocaleString("en-AE", {
                  maximumFractionDigits: 0,
                })}`
          }
          percentage={
            loading
              ? "..."
              : `${percentages.remainingCredit >= 0 ? "+" : ""}${
                  percentages.remainingCredit
                }%`
          }
          aedicon={<>{derhumSign}</>}
          icon={totalearning}
          bgStyle='linear-gradient(280.11deg,rgb(168, 85, 247) -12.68%,rgb(124, 58, 237) 100.28%)'
          svgPath='M0.999699 1.00016C26.9471 6.49534 97.1448 21.4249 87.4724 53.7082C80.8686 75.7493 56.5904 63.6007 64.7409 39.7722C72.8914 15.9437 115.65 15.7066 119.4 64.9586M119.4 64.9586L111.04 59.6056M119.4 64.9586L126.125 53.5401'
          svgPosition='absolute left-[2.5rem] sm:left-[4rem] md:left-[6rem] top-0'
        />
      </div>

      <div className='flex flex-col lg:flex-row w-full gap-4 sm:gap-5 md:gap-6 mb-4 sm:mb-6 md:mb-8'>
        {/* Left Panel - 65% width */}
        <div className='w-full lg:w-2/3 space-y-4 sm:space-y-5 md:space-y-6'>
          {/* Won Vehicles Section */}
          <div className='bg-white rounded-lg shadow p-1'>
            <div
              className={`flex items-center justify-between mb-1 bg-darkGraybg rounded-t-lg p-3 sm:p-4 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <h2 className='text-base sm:text-lg font-semibold text-white'>
                {translate("dashboard.stats.recentWonVehicles")}
              </h2>
              <button
                onClick={() => navigate("/wonvehicles")}
                className='text-white hover:text-gray-300 text-xs sm:text-sm font-medium'
              >
                {translate("dashboard.stats.showAll")}
              </button>
            </div>
            <div className='overflow-x-auto'>
              {/* Desktop Table View */}
              <table className='w-full hidden md:table'>
                <thead className='bg-gray-200 text-bg-darkgray'>
                  <tr className='border-b border-gray-200'>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.vehicleInfo")}
                    </th>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.lotNumber")}
                    </th>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.vinNumber")}
                    </th>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.price")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan='4'
                        className='py-6 sm:py-8 text-center text-xs sm:text-sm text-gray-500'
                      >
                        {translate("dashboard.stats.loadingWonVehicles")}
                      </td>
                    </tr>
                  ) : wonVehicles.length === 0 ? (
                    <tr>
                      <td
                        colSpan='4'
                        className='py-6 sm:py-8 text-center text-xs sm:text-sm text-gray-500'
                      >
                        {translate("dashboard.stats.noWonVehiclesFound")}
                      </td>
                    </tr>
                  ) : (
                    wonVehicles.map((vehicle, index) => (
                      <tr
                        key={vehicle.id || index}
                        className='border-b border-gray-100 hover:bg-gray-50'
                      >
                        <td className='py-2 md:py-3 px-2'>
                          <div
                            className={`flex items-center ${
                              isRTL
                                ? "space-x-reverse space-x-2 sm:space-x-3"
                                : "space-x-2 sm:space-x-3"
                            }`}
                          >
                            <div className='w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0'>
                              {vehicle.images && vehicle.images.length > 0 ? (
                                <img
                                  src={vehicle.images[0].imageUrl}
                                  alt='Vehicle'
                                  className='w-full h-full object-cover'
                                />
                              ) : (
                                <img
                                  src={placeholder}
                                  alt='Placeholder'
                                  className='w-full h-full object-cover'
                                />
                              )}
                            </div>
                            <div className='min-w-0 flex-1'>
                              <p className='font-medium text-gray-900 text-xs sm:text-sm truncate'>
                                {vehicle.name || "-"}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className='py-2 md:py-3 px-2 text-xs sm:text-sm text-gray-600'>
                          {vehicle.lotNumber ? `${vehicle.lotNumber}` : "-"}
                        </td>
                        <td className='py-2 md:py-3 px-2 text-xs sm:text-sm text-gray-600 truncate max-w-[120px]'>
                          {vehicle.vinNumber ? `${vehicle.vinNumber}` : "-"}
                        </td>
                        <td className='py-2 md:py-3 px-2'>
                          <span className='font-semibold text-gray-900 text-xs sm:text-sm'>
                            {formatPrice(vehicle.soldPrice || 0)}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className='md:hidden space-y-3 p-2'>
                {loading ? (
                  <div className='py-6 text-center text-sm text-gray-500'>
                    {translate("dashboard.stats.loadingWonVehicles")}
                  </div>
                ) : wonVehicles.length === 0 ? (
                  <div className='py-6 text-center text-sm text-gray-500'>
                    {translate("dashboard.stats.noWonVehiclesFound")}
                  </div>
                ) : (
                  wonVehicles.map((vehicle, index) => (
                    <div
                      key={vehicle.id || index}
                      className='bg-gray-50 rounded-lg p-3 border border-gray-200'
                    >
                      <div
                        className={`flex items-start ${
                          isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                        } mb-3`}
                      >
                        <div className='w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0'>
                          {vehicle.images && vehicle.images.length > 0 ? (
                            <img
                              src={vehicle.images[0].imageUrl}
                              alt='Vehicle'
                              className='w-full h-full object-cover'
                            />
                          ) : (
                            <img
                              src={placeholder}
                              alt='Placeholder'
                              className='w-full h-full object-cover'
                            />
                          )}
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='font-medium text-gray-900 text-sm mb-1 break-words'>
                            {vehicle.name || "-"}
                          </p>
                        </div>
                      </div>
                      <div className='grid grid-cols-2 gap-2 text-xs'>
                        <div>
                          <span className='text-gray-500'>
                            {translate("dashboard.stats.lotNumber")}:
                          </span>
                          <p className='font-medium text-gray-900'>
                            {vehicle.lotNumber || "-"}
                          </p>
                        </div>
                        <div>
                          <span className='text-gray-500'>
                            {translate("dashboard.stats.vinNumber")}:
                          </span>
                          <p className='font-medium text-gray-900 truncate'>
                            {vehicle.vinNumber || "-"}
                          </p>
                        </div>
                        <div className='col-span-2 pt-2 border-t border-gray-200'>
                          <span className='text-gray-500'>
                            {translate("dashboard.stats.price")}:
                          </span>
                          <p className='font-semibold text-gray-900 text-sm mt-0.5'>
                            {formatPrice(vehicle.soldPrice || 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Recent Service Charges Section */}
          <div className='bg-white rounded-lg shadow p-1'>
            <div
              className={`flex items-center justify-between mb-1 bg-darkGraybg rounded-t-lg p-3 sm:p-4 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <h2 className='text-base sm:text-lg font-semibold text-white'>
                {translate("dashboard.stats.recentServiceCharges")}
              </h2>
              <a
                href='#'
                className='text-white hover:text-gray-300 text-xs sm:text-sm font-medium'
              >
                {translate("dashboard.stats.showAll")}
              </a>
            </div>
            <div className='overflow-x-auto'>
              {/* Desktop Table View */}
              <table className='w-full hidden md:table'>
                <thead className='bg-gray-200 text-bg-darkgray'>
                  <tr className='border-b border-gray-200'>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.vehicleInfo")}
                    </th>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.lotNumber")}
                    </th>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.vinNumber")}
                    </th>
                    <th
                      className={`${
                        isRTL ? "text-right" : "text-left"
                      } py-2 md:py-3 px-2 text-xs sm:text-sm font-medium`}
                    >
                      {translate("dashboard.stats.serviceCharges")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[1, 2].map((item, index) => (
                    <tr
                      key={index}
                      className='border-b border-gray-100 hover:bg-gray-50'
                    >
                      <td className='py-2 md:py-3 px-2'>
                        <div
                          className={`flex items-center ${
                            isRTL
                              ? "space-x-reverse space-x-2 sm:space-x-3"
                              : "space-x-2 sm:space-x-3"
                          }`}
                        >
                          <div className='w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0'>
                            <span className='text-[10px] sm:text-xs text-gray-500'>
                              All Photos
                            </span>
                          </div>
                          <div>
                            <p className='font-medium text-gray-900 text-xs sm:text-sm'>
                              Hyundai Santa Cruz Sel
                            </p>
                            <p className='text-[10px] sm:text-xs text-gray-500'>
                              Model 2022
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className='py-2 md:py-3 px-2 text-xs sm:text-sm text-gray-600'>
                        Lotrgba(134, 121, 151, 0.4)
                      </td>
                      <td className='py-2 md:py-3 px-2 text-xs sm:text-sm text-gray-600 truncate max-w-[120px]'>
                        VIN #MG006897
                      </td>
                      <td className='py-2 md:py-3 px-2'>
                        <span className='font-semibold text-gray-900 text-xs sm:text-sm'>
                          320
                        </span>
                        <span className='text-gray-500 ml-1 text-xs sm:text-sm'>
                          AED
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className='md:hidden space-y-3 p-2'>
                {[1, 2].map((item, index) => (
                  <div
                    key={index}
                    className='bg-gray-50 rounded-lg p-3 border border-gray-200'
                  >
                    <div
                      className={`flex items-start ${
                        isRTL ? "space-x-reverse space-x-3" : "space-x-3"
                      } mb-3`}
                    >
                      <div className='w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0'>
                        <span className='text-xs text-gray-500 text-center px-1'>
                          All Photos
                        </span>
                      </div>
                      <div className='flex-1 min-w-0'>
                        <p className='font-medium text-gray-900 text-sm mb-0.5'>
                          Hyundai Santa Cruz Sel
                        </p>
                        <p className='text-xs text-gray-500'>Model 2022</p>
                      </div>
                    </div>
                    <div className='grid grid-cols-2 gap-2 text-xs'>
                      <div>
                        <span className='text-gray-500'>
                          {translate("dashboard.stats.lotNumber")}:
                        </span>
                        <p className='font-medium text-gray-900'>
                          Lotrgba(134, 121, 151, 0.4)
                        </p>
                      </div>
                      <div>
                        <span className='text-gray-500'>
                          {translate("dashboard.stats.vinNumber")}:
                        </span>
                        <p className='font-medium text-gray-900 truncate'>
                          VIN #MG006897
                        </p>
                      </div>
                      <div className='col-span-2 pt-2 border-t border-gray-200'>
                        <span className='text-gray-500'>
                          {translate("dashboard.stats.serviceCharges")}:
                        </span>
                        <p className='font-semibold text-gray-900 text-sm mt-0.5'>
                          320{" "}
                          <span className='text-gray-500 font-normal'>AED</span>
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - 35% width */}
        <div className='w-full lg:w-1/3'>
          <div className='bg-white rounded-lg shadow p-4 sm:p-5 md:p-6'>
            <div
              className={`flex flex-col sm:flex-row items-start sm:items-center justify-between mb-3 sm:mb-4 gap-2 sm:gap-0 ${
                isRTL ? "sm:flex-row-reverse" : ""
              }`}
            >
              <div>
                <h2 className='text-lg sm:text-xl font-semibold text-gray-800'>
                  {translate("dashboard.stats.paymentStatistic")}
                </h2>
                <p className='text-xs sm:text-sm text-gray-500'>
                  {translate("dashboard.stats.trackYourPayment")}
                </p>
              </div>
              <select
                className={`text-xs sm:text-sm border border-gray-300 rounded-lg ${
                  isRTL ? "pr-2 pl-2 sm:pr-3 sm:pl-3" : "px-2 sm:px-3"
                } py-1.5 sm:py-1 bg-white w-full sm:w-auto`}
              >
                <option>{translate("dashboard.stats.thisMonth")}</option>
              </select>
            </div>

            {/* Donut Chart */}
            <div className='flex justify-center mb-4 sm:mb-6'>
              <div className='relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32'>
                <svg
                  className='w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 transform -rotate-90'
                  viewBox='0 0 32 32'
                >
                  {/* Blue segment - 40% */}
                  <circle
                    cx='16'
                    cy='16'
                    r='14'
                    fill='none'
                    stroke='#3B82F6'
                    strokeWidth='4'
                    strokeDasharray='35.2 88'
                    strokeDashoffset='0'
                  />
                  {/* Green segment - 35% */}
                  <circle
                    cx='16'
                    cy='16'
                    r='14'
                    fill='none'
                    stroke='#10B981'
                    strokeWidth='4'
                    strokeDasharray='30.8 88'
                    strokeDashoffset='-35.2'
                  />
                  {/* Amber segment - 16% */}
                  <circle
                    cx='16'
                    cy='16'
                    r='14'
                    fill='none'
                    stroke='#F59E0B'
                    strokeWidth='4'
                    strokeDasharray='14.08 88'
                    strokeDashoffset='-66'
                  />
                  {/* Red segment - 10% */}
                  <circle
                    cx='16'
                    cy='16'
                    r='14'
                    fill='none'
                    stroke='#EF4444'
                    strokeWidth='4'
                    strokeDasharray='8.8 88'
                    strokeDashoffset='-80.08'
                  />
                  {/* Inner circle */}
                  <circle
                    cx='16'
                    cy='16'
                    r='8'
                    fill='#F5F5F5'
                    stroke='#E0E0E0'
                    strokeWidth='1'
                    strokeDasharray='2 2'
                  />
                </svg>
              </div>
            </div>

            {/* Earning Summary */}
            <div className='space-y-2 sm:space-y-3'>
              <div
                className={`flex justify-between items-center ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <span className='text-xs sm:text-sm text-gray-600'>
                  {translate("dashboard.stats.totalPaidAmount")}
                </span>
                <span
                  className={`font-semibold text-gray-900 text-xs sm:text-sm ${
                    isRTL ? "text-left" : "text-right"
                  } break-words`}
                >
                  {loading ? "..." : formatPrice(stats.totalPaidAmount)}
                </span>
              </div>
              <div
                className={`flex justify-between items-center ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <span className='text-xs sm:text-sm text-gray-600'>
                  {translate("dashboard.stats.totalVATPaid")}
                </span>
                <span
                  className={`font-semibold text-gray-900 text-xs sm:text-sm ${
                    isRTL ? "text-left" : "text-right"
                  } break-words`}
                >
                  {loading ? "..." : formatPrice(stats.totalVATPaid)}
                </span>
              </div>
              <div
                className={`flex justify-between items-center ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <span className='text-xs sm:text-sm text-gray-600'>
                  {translate("dashboard.stats.remainingDeposit")}
                </span>
                <span
                  className={`font-semibold text-gray-900 text-xs sm:text-sm ${
                    isRTL ? "text-left" : "text-right"
                  } break-words`}
                >
                  {loading ? "..." : formatPrice(stats.remainingDeposit)}
                </span>
              </div>
              <div
                className={`flex justify-between items-center pt-2 border-t border-gray-200 ${
                  isRTL ? "flex-row-reverse" : ""
                }`}
              >
                <span className='text-xs sm:text-sm font-medium text-gray-800'>
                  {translate("dashboard.stats.totalRemainingAmount")}
                </span>
                <span
                  className={`text-sm sm:text-base md:text-lg font-bold text-blue-600 ${
                    isRTL ? "text-left" : "text-right"
                  } break-words`}
                >
                  {loading ? "..." : formatPrice(stats.totalRemainingAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerDashboard;
